import {memo} from "react";
import PropTypes from "prop-types";

/**
 * The page's headline fact and its draw-eligibility status, in one card.
 *
 * The card's colour is the state: blue while the customer still has conditions
 * left, green once all are met. `count` is the potential total — the tickets
 * they've accumulated — shown in both states; when ineligible it is not yet in
 * the draw, which the checklist and note make explicit rather than hiding the
 * number behind a bare zero.
 *
 * Eligibility is derived from the requirements themselves, so the badge, the
 * checkmarks and the segment bar can never disagree with each other.
 */
const RowIcon = ({met}) =>
    met ? (
        <svg className="hp-hero-check-icon" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
            <circle cx="10" cy="10" r="10" fill="var(--hp-ok)"/>
            <path d="M5.8 10.4 8.6 13.1 14.2 7.1" fill="none" stroke="#fff" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    ) : (
        <span className="hp-hero-check-empty" aria-hidden="true"/>
    );

RowIcon.propTypes = {met: PropTypes.bool.isRequired};

const TicketHero = ({count, requirements, loading, onViewTickets}) => {
    const total = requirements.length;
    const metCount = requirements.filter(r => r.met).length;
    const eligible = metCount === total;

    const note = eligible
        ? `Бүх нөхцөл биелсэн. Таны ${count} эрх сугалаанд орсон.`
        : `Бага зэрэг үлдлээ — үлдсэн нөхцөлийг гүйцээснээр таны ${count} эрх сугалаанд орно.`;

    // Filled by count, left to right — the first `metCount` segments — so the
    // bar reads as progress. Colouring each segment by its own condition would
    // leave an empty segment stranded between filled ones whenever the unmet
    // condition isn't the last (e.g. transactions ✓, referral ✗, card ✓).
    const segments = (
        <div className="hp-hero-segments" aria-hidden="true">
            {requirements.map((_, i) => (
                <span key={i} className={`hp-hero-seg${i < metCount ? ' hp-hero-seg-on' : ''}`}/>
            ))}
        </div>
    );

    return (
        <section className={`hp-hero ${eligible ? 'hp-hero-eligible' : 'hp-hero-pending'}`}>
            <div className="hp-hero-top">
                <div className="hp-hero-main">
                    <h2 className="hp-label">Нийт урамшууллын эрх</h2>
                    <p className="hp-hero-count">
                        <b className="hp-figure">{count}</b>
                        <span className="hp-hero-unit">ширхэг</span>
                    </p>
                </div>

                <div className="hp-hero-badge">
                    {eligible ? (
                        <>
                            <span className="hp-hero-badge-title">✓ Сугалаанд хамрагдсан</span>
                            <span className="hp-hero-badge-sub">{metCount} / {total} нөхцөл</span>
                        </>
                    ) : (
                        <>
                            <span className="hp-hero-badge-title">Хараахан хамрагдаагүй</span>
                            <span className="hp-hero-badge-sub">{total - metCount} нөхцөл дутуу</span>
                        </>
                    )}
                </div>
            </div>

            {eligible ? (
                <div className="hp-hero-body">
                    {segments}
                    <p className="hp-hero-note">{note}</p>
                </div>
            ) : (
                <div className="hp-hero-panel">
                    <h3 className="hp-hero-panel-title">
                        Сугалаанд хамрагдахын тулд {total} нөхцөл бүгд шаардлагатай
                    </h3>

                    <ul className="hp-hero-checklist">
                        {requirements.map((r, i) => (
                            <li key={i} className={`hp-hero-check ${r.met ? 'is-met' : 'is-unmet'}`}>
                                <RowIcon met={r.met}/>
                                <span className="hp-hero-check-label">{r.label}</span>
                                <span className="hp-hero-check-value">{r.valueText}</span>
                            </li>
                        ))}
                    </ul>

                    {segments}
                    <p className="hp-hero-panel-note">{note}</p>
                </div>
            )}

            <div className="hp-hero-foot">
                <button
                    type="button"
                    className="hp-hero-btn"
                    onClick={onViewTickets}
                    disabled={loading}
                >
                    {loading ? 'Уншиж байна…' : 'Урамшууллын эрх харах'}
                </button>
            </div>
        </section>
    );
};

TicketHero.propTypes = {
    count: PropTypes.number.isRequired,
    requirements: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        met: PropTypes.bool.isRequired,
        valueText: PropTypes.string.isRequired,
    })).isRequired,
    loading: PropTypes.bool,
    onViewTickets: PropTypes.func.isRequired,
};

export default memo(TicketHero);
